<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import fromUnixTime from 'date-fns/fromUnixTime';
import { useReportFormatter } from 'dashboard/composables/useReportFormatter';
import BarChart from 'shared/components/charts/BarChart.vue';
import { DEFAULT_CHART } from '../../constants';

// A aba Carrinhos de Relatórios. Um carrinho é um link de checkout mandado
// dentro da conversa. A conta diz qual link é esse; sem isso o backend responde
// 422 e a tela mostra o motivo, em vez de um gráfico zerado que leria como
// "ninguém vendeu". A página pede (uma vez por filtro, como as outras abas), no
// período do filtro comum, por dia, e com "Incluir testes" (G10).
const props = defineProps({
  // A resposta de GET /api/v2/reports?metric=cart_links_count; null enquanto
  // não chegou.
  report: { type: Array, default: null },
  loading: { type: Boolean, default: false },
  failed: { type: Boolean, default: false },
  // O erro do pedido: o 422 traz o motivo.
  error: { type: Object, default: null },
});

const { t } = useI18n();
const { shortDate } = useReportFormatter();

const rows = computed(() => props.report || []);
const errorMessage = computed(() =>
  props.failed
    ? props.error?.response?.data?.message || t('REPORT.CARTS.FETCH_FAILED')
    : ''
);

const total = computed(() =>
  rows.value.reduce((acc, row) => acc + Number(row.value || 0), 0)
);

const bucketLabel = timestamp => shortDate(fromUnixTime(timestamp));

const collection = computed(() => ({
  labels: rows.value.map(row => bucketLabel(row.timestamp)),
  datasets: DEFAULT_CHART.datasets.map(dataset => ({
    ...dataset,
    yAxisID: 'y',
    label: t('REPORT.CARTS.METRIC'),
    data: rows.value.map(row => row.value),
  })),
}));

const chartOptions = { scales: DEFAULT_CHART.scales };
</script>

<template>
  <div class="flex flex-col" data-test-id="atendimento-carrinhos">
    <div
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <div class="text-n-slate-11">
        <span class="text-sm">{{ $t('REPORT.CARTS.METRIC') }}</span>
        <div class="text-xl font-medium text-n-slate-12">
          {{ total.toLocaleString() }}
        </div>
        <p class="mt-1 text-xs">{{ $t('REPORT.CARTS.DESC') }}</p>
      </div>
      <div class="mt-4 h-72">
        <woot-loading-state
          v-if="loading"
          class="text-xs"
          :message="$t('REPORT.LOADING_CHART')"
        />
        <div v-else class="flex items-center justify-center h-72">
          <span v-if="errorMessage" class="text-sm text-n-ruby-10">
            {{ errorMessage }}
          </span>
          <BarChart
            v-else-if="rows.length"
            :collection="collection"
            :chart-options="chartOptions"
          />
          <span v-else class="text-sm text-n-slate-10">
            {{ $t('REPORT.NO_ENOUGH_DATA') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
